import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmt, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Minus, Plus, ClipboardList, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function PileStepper({ label, pileSize, value, onChange, testid }) {
  const v = Number(value) || 0;
  const set = (n) => onChange(Math.max(0, Math.round(n)));
  return (
    <div className="rounded-xl border-2 border-emerald-200 bg-white p-3" data-testid={`${testid}-row`}>
      <div className="flex items-baseline justify-between mb-2">
        <span className="font-display font-bold text-base text-slate-800">{label}</span>
        <span className="text-xs font-data text-slate-500">Unité&nbsp;: piles</span>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => set(v - 1)}
          className="tactile h-14 w-14 shrink-0 rounded-xl bg-red-100 text-red-800 flex items-center justify-center active:bg-red-200"
          data-testid={`${testid}-decrease`} aria-label="Diminuer">
          <Minus className="h-7 w-7" strokeWidth={3} />
        </button>
        <input type="number" inputMode="numeric" value={v === 0 ? "" : v} placeholder="0"
          onChange={(e) => set(parseInt(e.target.value || "0", 10))}
          className="h-14 flex-1 min-w-0 rounded-xl border-2 border-emerald-200 bg-slate-50 text-center font-data text-3xl font-black text-emerald-900 focus:outline-none focus:ring-4 focus:ring-emerald-200"
          data-testid={`${testid}-value`} />
        <button type="button" onClick={() => set(v + 1)}
          className="tactile h-14 w-14 shrink-0 rounded-xl bg-green-100 text-green-800 flex items-center justify-center active:bg-green-200"
          data-testid={`${testid}-increase`} aria-label="Augmenter">
          <Plus className="h-7 w-7" strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}

export default function BesoinDialog({ open, onOpenChange, onDone, fermeName }) {
  const [qty, setQty] = useState({});
  const [commentaire, setCommentaire] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const { data: types = [] } = useQuery({
    queryKey: ["packaging-types"],
    queryFn: async () => (await api.get("/packaging-types")).data,
    enabled: open,
  });

  useEffect(() => {
    if (open) { setQty({}); setCommentaire(""); setDone(false); }
  }, [open]);

  const submit = async () => {
    const lines = types.map((t) => ({ packaging_type: t.code, piles: Number(qty[t.code]) || 0 }))
      .filter((l) => l.piles > 0);
    if (lines.length === 0) { toast.error("Indiquez au moins une quantité."); return; }
    setSaving(true);
    try {
      await api.post("/besoins", { lines, commentaire });
      setDone(true);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="besoin-dialog">
        {done ? (
          <div className="py-6 text-center space-y-3" data-testid="besoin-success">
            <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto" />
            <h3 className="font-display font-extrabold text-xl text-slate-900">Besoin enregistré</h3>
            <p className="text-sm text-slate-600">La logistique d'ANNEZIN pourra anticiper votre approvisionnement.</p>
            <Button className="h-12 w-full bg-emerald-700 hover:bg-emerald-800 text-white font-bold" onClick={() => onOpenChange(false)} data-testid="besoin-close-button">
              Fermer
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-emerald-700" />
                Déclaration de besoin{fermeName ? ` — ${fermeName}` : ""}
              </DialogTitle>
              <DialogDescription>Indiquez les quantités d'emballages propres dont vous avez besoin (en piles).</DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-1">
              {types.map((t) => (
                <PileStepper key={t.code} label={t.label} pileSize={t.pile_size || 1}
                  value={qty[t.code] || 0}
                  onChange={(n) => setQty((p) => ({ ...p, [t.code]: n }))}
                  testid={`besoin-${t.code}`} />
              ))}
              <Textarea placeholder="Commentaire (optionnel) — précisez une urgence, une date souhaitée…"
                value={commentaire} onChange={(e) => setCommentaire(e.target.value)}
                data-testid="besoin-commentaire" className="min-h-[64px]" />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="h-12">Annuler</Button>
              <Button onClick={submit} disabled={saving} className="h-12 bg-emerald-700 hover:bg-emerald-800 text-white font-bold" data-testid="submit-besoin-button">
                {saving ? "Enregistrement…" : "Valider le besoin"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
